import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { studentApi } from '@/api';
import { Card, CardHeader, Icon, ProgressBar, Skeleton } from '@/components/ui';
import { paths } from '@/routes/paths';

/**
 * Where each missing part is fixed. Parts edited on the profile form itself
 * have no link: the student is already on that page.
 */
const FIX_LINKS = {
  education: { to: paths.student.education, label: 'Add education' },
  experience: { to: paths.student.experience, label: 'Add experience' },
  skills: { to: paths.student.skills, label: 'Add skills' },
  resume: { to: paths.student.cv, label: 'Upload a CV' },
};

/**
 * "Completion" with what is actually missing, from the server's breakdown,
 * instead of one fixed sentence that kept naming parts already done.
 *
 * `percentage` is the profile's stored figure; it keys the query, so the
 * breakdown refetches whenever a save changes the number.
 */
export function ProfileCompletionCard({ percentage }) {
  const { data, isPending } = useQuery({
    queryKey: ['student-completion', percentage],
    queryFn: studentApi.getCompletion,
  });

  const sections = data?.sections ?? [];
  const missing = sections.filter((section) => !section.complete);
  const done = sections.filter((section) => section.complete);
  const value = data?.percentage ?? percentage ?? 0;

  return (
    <Card>
      <CardHeader title="Completion" description="How complete companies see you." />
      <ProgressBar value={value} label="Profile" />

      {isPending ? (
        <Skeleton className="mt-4 h-16 w-full" />
      ) : missing.length === 0 ? (
        <p className="text-success-700 mt-3 flex items-center gap-1.5 text-sm font-medium">
          <Icon name="check" size={15} />
          Your profile is complete.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          <p className="text-ink text-sm font-medium">
            {missing.length === 1 ? 'One thing left' : `${missing.length} things left`} to reach
            100%
          </p>
          <ul className="space-y-2.5">
            {missing.map((section) => {
              const fix = FIX_LINKS[section.key];
              return (
                <li key={section.key} className="flex items-start gap-2.5">
                  <span className="border-line mt-0.5 size-4 shrink-0 rounded-full border-2" />
                  <div className="min-w-0 flex-1">
                    <p className="text-ink flex flex-wrap items-baseline justify-between gap-x-2 text-sm font-medium">
                      {section.label}
                      <span className="text-ink-muted text-xs font-normal">+{section.weight}%</span>
                    </p>
                    {section.hint && <p className="text-ink-muted text-xs">{section.hint}</p>}
                    {fix && (
                      <Link
                        to={fix.to}
                        className="text-brand-700 mt-0.5 inline-flex items-center gap-1 text-xs font-medium hover:underline"
                      >
                        {fix.label}
                        <Icon name="arrowRight" size={12} />
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          {done.length > 0 && (
            <p className="text-ink-muted border-line border-t pt-3 text-xs">
              <span className="text-success-700 font-medium">Done: </span>
              {done.map((section) => section.label).join(' · ')}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
